import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listSg12, getSg12Stats, getSg12ZmenyCen, schvalitSg12, skrytSg12,
  type Sg12Karta,
} from "../lib/api.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { AlertIcon } from "../lib/icons.js";

/**
 * SG12.cz — GAERNE Cross / Enduro (dovozce TSS-TECHNICAL SPORTS s. r. o.).
 *
 * Rozsah rozhodl Vít 24. 8. 2026: *„jde mi jen o Cross Enduro boty. jiné mě
 * nezajímají."* → 11 podkategorií sekce Cross / Enduro + kusy z Výprodeje
 * téže modelové řady. Racing, Touring a Adventure se nesbírají.
 *
 * 🔑 JEDEN REF = JEDNA KARTA na eshopu, velikosti jsou její varianty —
 * stejná stavba jako boty Acerbis, které na eshopu už máme.
 *
 * 💰 Tři čísla vedle sebe: ceníková dovozce · za kolik dovozce PRODÁVÁ ·
 * naše cena. ⏳ Cenotvorba není rozhodnutá (Vít: *„na eshop se pak
 * domluvíme"*), takže návrh je zatím MOC z ABRY a jde přepsat ručně.
 *
 * ⚠ Sloupec „vs dovozce" je HLÍDAČ, ne ozdoba: změřeno 24. 8. na 8 botách,
 * že MOC z ABRY se rovná PŘESNĚ ceníkové ceně dovozce — ale on sám prodává
 * 19–48 % pod ní. Zákazník má jeho e-shop na dvě kliknutí z Googlu.
 */

const kc = (v: string | number | null): string =>
  v === null ? "—" : `${Math.round(Number(v)).toLocaleString("cs-CZ")} Kč`;

const datum = (s: string | null): string =>
  s === null ? "" : new Date(s).toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric" });

function StavBadge({ stav }: { stav: Sg12Karta["stav"] }): JSX.Element | null {
  if (stav === "na-eshopu") return <Badge tone="green">Na eshopu</Badge>;
  if (stav === "ceka") return <Badge tone="blue">Čeká na robota</Badge>;
  if (stav === "skryto") return <Badge tone="neutral">Skryto</Badge>;
  if (stav === "chyba") return <Badge tone="red">Chyba</Badge>;
  return null;
}

function Karta({ k, onZmena }: { k: Sg12Karta; onZmena: () => void }): JSX.Element {
  const [pracuje, setPracuje] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);

  const akce = async (fn: (ref: string) => Promise<void>): Promise<void> => {
    setPracuje(true);
    setChyba(null);
    try {
      await fn(k.ref);
      onZmena();
    } catch (e) {
      setChyba(e instanceof Error ? e.message : "nepovedlo se");
    } finally {
      setPracuje(false);
    }
  };

  // Hlídač: kolik procent jsme nad tím, za co to prodává sám dovozce.
  const nad = k.odchylkaProtiDovozci;
  const drazsi = nad !== null && nad > 0.05;

  return (
    <div className="group bg-white rounded-xl border border-zinc-200 overflow-hidden transition-all hover:border-ktm-orange hover:shadow-md flex flex-col">
      <Link to={`/sg12/${encodeURIComponent(k.ref)}`} className="block">
        <div className="aspect-[4/3] bg-zinc-50 relative overflow-hidden">
          {k.fotka ? (
            <img
              src={k.fotka}
              alt={k.nazev}
              loading="lazy"
              className="w-full h-full object-contain p-2 group-hover:scale-[1.03] transition-transform"
            />
          ) : (
            <div className="w-full h-full grid place-items-center text-zinc-300 text-xs">bez fotky</div>
          )}
          <div className="absolute bottom-1.5 left-1.5 flex gap-1 flex-wrap">
            {/* ⚠ Přeškrtnutou cenu má u dovozce VŠECH 66 karet (sleva 11–56 %,
                medián 26 %) — kdyby se štítek dával všem, neznamenal by nic.
                Vypichuje se až 30 % a víc. */}
            {(k.slevaPct ?? 0) >= 30 && <Badge tone="red">Sleva −{k.slevaPct} %</Badge>}
            {!k.vedemeVAbre && <Badge tone="amber">Nevedeme</Badge>}
            {k.skladVlasim > 0 && <Badge tone="green">Vlašim {k.skladVlasim} ks</Badge>}
            <StavBadge stav={k.stav} />
          </div>
          {/* Příslušenství velikosti nemá — dlaždice by hlásila „0/0 vel." */}
          {!k.jePrislusenstvi && (
            <div className="absolute top-1.5 right-1.5 rounded bg-white/90 border border-zinc-200 px-1 py-0.5 text-[10px] text-zinc-600">
              {k.velikostiUDovozce}/{k.velikostiCelkem} vel.
            </div>
          )}
        </div>
      </Link>

      <div className="p-2.5 flex-1 flex flex-col">
        <div className="text-[11px] text-zinc-400 truncate">
          {k.rada} · <span className="font-mono">{k.ref}</span>
        </div>
        <Link
          to={`/sg12/${encodeURIComponent(k.ref)}`}
          className="mt-0.5 text-[13px] font-medium text-zinc-900 line-clamp-2 min-h-[2.2rem] leading-snug hover:text-ktm-orange"
        >
          {k.nazev}
        </Link>

        {/* 💰 Dovozce × my. Bez obojího vedle sebe se nedá poznat, jestli
            jsme konkurenceschopní — ceníkovou cenu neúčtuje ani on sám. */}
        <div className="mt-1.5 text-[12px] space-y-0.5">
          <div className="flex justify-between text-zinc-500">
            <span>dovozce prodává</span>
            <span className="tabular-nums">{kc(k.prodavaDovozceZa)}</span>
          </div>
          {k.cenikovaDovozce !== null && k.vAkci && (
            <div className="flex justify-between text-zinc-400">
              <span>jeho ceníková</span>
              <span className="tabular-nums line-through">{kc(k.cenikovaDovozce)}</span>
            </div>
          )}
          <div className="flex justify-between font-semibold text-zinc-900">
            <span>naše</span>
            <span className="tabular-nums">
              {kc(k.naseCena)}
              {k.cenaRucne && <span className="ml-1 text-amber-600" title="ruční cena">✎</span>}
            </span>
          </div>
          {nad !== null && (
            <div className={`flex justify-between ${drazsi ? "text-amber-700" : "text-zinc-400"}`}>
              <span>{drazsi && <AlertIcon size={11} className="inline mr-0.5" />}vs dovozce</span>
              <span className="tabular-nums">
                {nad > 0 ? "+" : ""}
                {Math.round(nad * 100)} %
              </span>
            </div>
          )}
        </div>

        {k.rozporVelikosti > 0 && (
          <div
            className="mt-1 text-[10px] text-amber-700"
            title="Karta dovozce a jeho tabulka Dostupné velikosti hlásí jiný sklad. Neslučujeme to."
          >
            ⚠ {k.rozporVelikosti}× rozpor ve velikostech
          </div>
        )}
        {k.cenaZmenaKdy && (
          <div className="mt-0.5 text-[10px] text-zinc-400">cena se hnula {datum(k.cenaZmenaKdy)}</div>
        )}

        <div className="mt-2 pt-2 border-t border-zinc-100 flex items-center gap-1">
          {k.stav === "na-eshopu" || k.stav === "ceka" ? (
            <Button size="sm" variant="ghost" disabled={pracuje} onClick={() => void akce(skrytSg12)}>
              Skrýt z eshopu
            </Button>
          ) : (
            <Button size="sm" disabled={pracuje} onClick={() => void akce(schvalitSg12)}>
              Na eshop
            </Button>
          )}
          <Link
            to={`/sg12/${encodeURIComponent(k.ref)}`}
            className="ml-auto text-[11px] text-zinc-400 hover:text-ktm-orange"
          >
            detail
          </Link>
        </div>
        {chyba && <div className="text-[11px] text-red-600 mt-1">{chyba}</div>}
      </div>
    </div>
  );
}

/** Panel „co se u dovozce pohnulo" — zadání Víta *„hlídat jejich ceny, akce"*. */
function ZmenyCen(): JSX.Element | null {
  const [data, setData] = useState<Awaited<ReturnType<typeof getSg12ZmenyCen>> | null>(null);
  const [otevreno, setOtevreno] = useState(false);

  useEffect(() => {
    void getSg12ZmenyCen(30).then(setData).catch(() => setData(null));
  }, []);

  if (!data || data.zmeny.length === 0) return null;

  return (
    <section className="rounded-xl border border-zinc-200 bg-white">
      <button
        type="button"
        onClick={() => setOtevreno((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left"
      >
        <span className="font-medium text-sm">Změny cen u dovozce</span>
        <Badge tone="blue">{data.zmeny.length} za {data.dnu} dní</Badge>
        <span className="ml-auto text-zinc-400 text-sm">{otevreno ? "▲" : "▼"}</span>
      </button>
      {otevreno && (
        <div className="px-4 pb-3 overflow-x-auto">
          <table className="w-full min-w-[40rem] text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-zinc-400">
                <th className="py-2 pr-3 text-left">Kdy</th>
                <th className="py-2 pr-3 text-left">Karta</th>
                <th className="py-2 pr-3 text-right">Nová cena</th>
                <th className="py-2 pr-3 text-right">Rozdíl</th>
                <th className="py-2 text-left">Akce</th>
              </tr>
            </thead>
            <tbody>
              {data.zmeny.map((z, i) => {
                const r = z.rozdil === null ? 0 : Number(z.rozdil);
                return (
                  <tr key={`${z.ref}-${i}`} className="border-t border-zinc-100">
                    <td className="py-1.5 pr-3 text-zinc-500 whitespace-nowrap">
                      {new Date(z.kdy).toLocaleDateString("cs-CZ")}
                    </td>
                    <td className="py-1.5 pr-3">
                      <Link to={`/sg12/${encodeURIComponent(z.ref)}`} className="hover:text-ktm-orange">
                        {z.nazev}
                      </Link>
                      <span className="ml-1 text-zinc-400 font-mono text-xs">{z.ref}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{kc(z.cena)}</td>
                    <td
                      className={`py-1.5 pr-3 text-right tabular-nums ${
                        r < 0 ? "text-green-700" : r > 0 ? "text-red-700" : "text-zinc-400"
                      }`}
                    >
                      {r === 0 ? "—" : `${r > 0 ? "+" : ""}${Math.round(r).toLocaleString("cs-CZ")} Kč`}
                    </td>
                    <td className="py-1.5">{z.vAkci ? <Badge tone="red">v akci</Badge> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function Sg12Page(): JSX.Element {
  const [karty, setKarty] = useState<Sg12Karta[] | null>(null);
  const [stats, setStats] = useState<Awaited<ReturnType<typeof getSg12Stats>> | null>(null);
  const [rada, setRada] = useState<string | null>(null);
  const [jenAkce, setJenAkce] = useState(false);
  const [jenVedeme, setJenVedeme] = useState(false);
  const [hledat, setHledat] = useState("");
  const [loading, setLoading] = useState(false);

  const nacti = useCallback(async () => {
    setLoading(true);
    try {
      const dotaz: Parameters<typeof listSg12>[0] = { jenAkce, jenVedeme };
      if (rada) dotaz.rada = rada;
      if (hledat.trim()) dotaz.hledat = hledat.trim();
      const r = await listSg12(dotaz);
      setKarty(r.karty);
    } finally {
      setLoading(false);
    }
  }, [rada, jenAkce, jenVedeme, hledat]);

  useEffect(() => { void nacti(); }, [nacti]);
  useEffect(() => { void getSg12Stats().then(setStats).catch(() => setStats(null)); }, []);

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-xl font-semibold">GAERNE — Cross / Enduro boty</h1>
        <p className="text-sm text-slate-500 mt-1">
          Dovozce <strong>sg12.cz</strong> (TSS-TECHNICAL SPORTS s. r. o.). Jeden REF = jedna
          karta na eshopu, velikosti jsou varianty. Cena z ABRY je zatím jen návrh —
          vedle ní je vidět, za kolik boty prodává sám dovozce.
        </p>
      </header>

      {stats && (
        <div className="flex flex-wrap gap-4 text-sm text-slate-600">
          <span>Na eshopu: <strong>{stats.naEshopu}</strong></span>
          <span>Čeká na robota: <strong>{stats.ceka}</strong></span>
          <span>Vedeme v ABŘE: <strong>{stats.vedeme}</strong></span>
          <span>
            Sleva u dovozce 30 %+: <strong className="text-red-700">{stats.vyraznaSleva}</strong>
            <span className="text-slate-400"> (přeškrtnutou cenu má {stats.vAkci} ze všech)</span>
          </span>
          {stats.posledniBeh && (
            <span className="text-slate-400">
              sběr {new Date(stats.posledniBeh.kdy).toLocaleString("cs-CZ")} ·{" "}
              {stats.posledniBeh.karet} karet
              {stats.posledniBeh.zmenaCeny > 0 && ` · ${stats.posledniBeh.zmenaCeny}× změna ceny`}
            </span>
          )}
        </div>
      )}

      <ZmenyCen />

      <div className="flex flex-wrap items-center gap-2">
        {stats?.rady.map((r) => (
          <Button
            key={r.rada}
            size="sm"
            variant={rada === r.rada ? "primary" : "ghost"}
            onClick={() => setRada(rada === r.rada ? null : r.rada)}
          >
            {r.rada}
            <span className="ml-1 opacity-60">{r.pocet}</span>
          </Button>
        ))}
        <span className="mx-2 h-5 w-px bg-slate-200" />
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={jenAkce} onChange={(e) => setJenAkce(e.target.checked)} />
          jen sleva 30 %+
        </label>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={jenVedeme} onChange={(e) => setJenVedeme(e.target.checked)} />
          jen co vedeme v ABŘE
        </label>
        <input
          value={hledat}
          onChange={(e) => setHledat(e.target.value)}
          placeholder="název, kód, barva…"
          className="ml-auto w-64 rounded border border-slate-300 px-3 py-1.5 text-sm"
        />
      </div>

      {loading && !karty ? <LoadingBlock /> : null}
      {karty && karty.length === 0 ? (
        <EmptyState
          title="Nic tu není"
          description="Zkus jiný filtr, nebo ještě neproběhl sběr z sg12.cz (pnpm sg12:import)."
        />
      ) : null}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {karty?.map((k) => <Karta key={k.ref} k={k} onZmena={() => void nacti()} />)}
      </div>
    </div>
  );
}
